<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type PropType, type VNode } from 'vue';
import {
  type DataTableColumns,
  type DataTableSortState,
  type PaginationProps,
  type SelectOption,
} from 'naive-ui';
import { searchReview, updateReview, type ReviewInDb, type ReviewSearchQuery } from '@/api';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import {
  AGENT_EMAIL,
  AGENT_LABEL,
  UNASSIGNED_VALUE,
  type ReviewFilterOptions,
} from '@/composables/review/useReviewSearchFilters';
import type { SortDirection } from '@/composables/useListQuerySync';
import { useAuthStore } from '@/stores/auth';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useReviewStore } from '@/stores/reviewStore';
import { initialsOf } from '@/utils/initials';
import { safeStringify } from '@/utils/jsonHelpers';
import { logger } from '@/utils/logger';
import { IconMore } from '@/components/design/icons';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  search: {
    type: String,
    default: '',
  },
  columnFilters: {
    type: Object as PropType<Record<string, string[]>>,
    default: () => ({}),
  },
  page: {
    type: Number,
    default: 1,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  sortKey: {
    type: String,
    default: 'name',
  },
  sortDirection: {
    type: String as PropType<SortDirection>,
    default: 'asc',
  },
  sortActivated: {
    type: Boolean,
    default: false,
  },
  includeArchived: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:page': [page: number];
  'update:pageSize': [size: number];
  /** `false` for `activated` means the user cleared the sort. */
  sort: [payload: { key: string; direction: SortDirection; activated: boolean }];
  open: [review: ReviewInDb];
  delete: [review: ReviewInDb];
  details: [review: ReviewInDb];
  'filter-options': [options: ReviewFilterOptions];
  /** Totals for the header line: how many reviews, and how many still running. */
  stats: [stats: { total: number; running: number }];
}>();

const authStore = useAuthStore();
const kanbanStore = useKanbanStore();
const organizationStore = useOrganizationStore();
const reviewStore = useReviewStore();
const message = useGaiaToast();

const reviews = ref<ReviewInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const editingCell = ref<{ uid: string; field: 'state' | 'user' } | null>(null);

const canEdit = computed(() => authStore.userPermissions.can_update_reviews);

// ── Data ────────────────────────────────────────────────────────────────────

async function fetchReviews(): Promise<void> {
  loading.value = true;
  try {
    const query: ReviewSearchQuery = {
      text: props.search,
      limit: props.pageSize,
      offset: (props.page - 1) * props.pageSize,
      sort_key: props.sortKey,
      sort_direction: props.sortDirection,
      similarity_threshold: 0.5,
      column_filters: props.columnFilters,
      include_archived_tickets: props.includeArchived,
    };

    const response = await searchReview({ body: query });
    if (!response.data) throw new Error('Failed to search reviews');

    reviews.value = response.data.results;
    totalCount.value = response.data.result_count;
    emit('stats', {
      total: totalCount.value,
      // Only the page on screen can be inspected for progress.
      running: reviews.value.filter(
        review =>
          (review.total_count ?? 0) > 0 && (review.finished_count ?? 0) < (review.total_count ?? 0)
      ).length,
    });

    const converted: ReviewFilterOptions = {};
    for (const [key, values] of Object.entries(response.data.filter_options)) {
      converted[key] = values.map(v => (v === null || v === undefined ? null : safeStringify(v)));
    }
    emit('filter-options', converted);
  } catch (error) {
    logger.error('Error fetching reviews:', error);
    message.error('Failed to load reviews');
  } finally {
    loading.value = false;
  }
}

watch(
  () =>
    [
      props.search,
      props.columnFilters,
      props.page,
      props.pageSize,
      props.sortKey,
      props.sortDirection,
      props.includeArchived,
    ] as const,
  () => void fetchReviews(),
  { deep: true }
);

const paginationConfig = computed<PaginationProps>(() => ({
  page: props.page,
  pageSize: props.pageSize,
  itemCount: totalCount.value,
  showSizePicker: true,
  pageSizes: [5, 10, 25],
  onUpdatePage: (page: number): void => {
    emit('update:page', page);
  },
  onUpdatePageSize: (size: number): void => {
    emit('update:pageSize', size);
    emit('update:page', 1);
  },
}));

function handleSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;

  if (sorter.order === false) {
    emit('sort', { key: 'name', direction: 'asc', activated: false });
    return;
  }

  // The state column sorts on the FK, not the display name.
  const key = sorter.columnKey === 'kanban_state' ? 'kanban_state_id' : String(sorter.columnKey);
  emit('sort', {
    key,
    direction: sorter.order === 'ascend' ? 'asc' : 'desc',
    activated: true,
  });
}

// ── Inline edits ────────────────────────────────────────────────────────────

const kanbanStateOptions = computed<SelectOption[]>(() =>
  kanbanStore.sortedKanbanStates.map(state => ({ label: state.name, value: state.uid }))
);

const userOptions = computed<SelectOption[]>(() => [
  { label: 'Unassigned', value: UNASSIGNED_VALUE },
  { label: AGENT_LABEL, value: AGENT_EMAIL },
  ...organizationStore.users.map(user => ({
    label: user.full_name || user.email,
    value: user.email,
  })),
]);

async function handleKanbanStateChange(reviewUid: string, newStateUid: string): Promise<void> {
  if (!canEdit.value) {
    message.error('You do not have permission to update reviews.');
    return;
  }
  try {
    // One call, same path the board's drop takes: update-sort-indexes writes the
    // state *and* the position, where a full-replace `updateReview` would move
    // the review but leave its old sort position behind.
    await kanbanStore.moveReview(reviewUid, newStateUid, 0);
    message.success('Kanban state updated');
    await fetchReviews();
  } catch (error) {
    logger.error('Error updating kanban state:', error);
    message.error('Failed to update kanban state');
  }
}

async function handleAssignedUserChange(
  reviewUid: string,
  newUserEmail: string | null
): Promise<void> {
  if (!canEdit.value) {
    message.error('You do not have permission to update reviews.');
    return;
  }
  const review = reviews.value.find(r => r.uid === reviewUid);
  if (!review) return;

  try {
    await updateReview({
      path: { review_uid: reviewUid },
      body: {
        name: review.name,
        description: review.description,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        kanban_column_sorting_position: review.kanban_column_sorting_position || null,
        assignee_user_email: newUserEmail,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        created_by_user_email: review.created_by_user_email || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        reporter_user_email: review.reporter_user_email || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        kanban_state_uid: review.kanban_state?.uid || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        due_date: review.due_date || null,
      },
    });
    message.success('Assigned user updated');
    await fetchReviews();
  } catch (error) {
    logger.error('Error updating assigned user:', error);
    message.error('Failed to update assigned user');
  }
}

async function deleteReview(review: ReviewInDb): Promise<void> {
  try {
    const success = await reviewStore.deleteReview(review.uid);
    if (success) {
      message.success('Review deleted successfully');
      await fetchReviews();
    }
  } catch (error) {
    logger.error('Error deleting review:', error);
    message.error('Failed to delete review');
  }
}

// ── Columns ─────────────────────────────────────────────────────────────────

function sortOrderFor(apiKey: string): 'ascend' | 'descend' | false {
  if (!props.sortActivated || props.sortKey !== apiKey) return false;
  return props.sortDirection === 'asc' ? 'ascend' : 'descend';
}

/** Enter edit mode without the row's own click handler also firing. */
function startEdit(event: Event, uid: string, field: 'state' | 'user'): void {
  event.stopPropagation();
  if (canEdit.value) editingCell.value = { uid, field };
}

const columns = computed<DataTableColumns<ReviewInDb>>(() => [
  {
    title: 'Name',
    key: 'name',
    width: 300,
    sortOrder: sortOrderFor('name'),
    sorter: true,
    render: (row): VNode => h(GaiaText, { weight: 'medium', truncate: true }, () => row.name),
  },
  {
    title: 'Kanban State',
    key: 'kanban_state',
    width: 180,
    sortOrder: sortOrderFor('kanban_state_id'),
    sorter: true,
    render(row): VNode {
      const isEditing = editingCell.value?.uid === row.uid && editingCell.value.field === 'state';

      if (isEditing) {
        return h(GaiaSelect, {
          value: row.kanban_state?.uid ?? null,
          options: kanbanStateOptions.value,
          size: 'small',
          placeholder: 'Select state',
          style: { minWidth: '160px' },
          disabled: !canEdit.value,
          onUpdateValue: (value: string) => {
            void handleKanbanStateChange(row.uid, value);
            editingCell.value = null;
          },
          onBlur: () => {
            editingCell.value = null;
          },
          onClick: (event: Event) => {
            event.stopPropagation();
          },
        });
      }

      if (!row.kanban_state) {
        return h(
          GaiaText,
          {
            size: 'meta',
            tone: 'muted',
            style: { cursor: canEdit.value ? 'pointer' : 'default' },
            onClick: (event: Event) => {
              startEdit(event, row.uid, 'state');
            },
          },
          () => 'No state'
        );
      }

      return h(
        GaiaStatusChip,
        {
          tone: 'neutral',
          size: 'small',
          style: { cursor: canEdit.value ? 'pointer' : 'default' },
          onClick: (event: Event) => {
            startEdit(event, row.uid, 'state');
          },
        },
        () => row.kanban_state?.name ?? ''
      );
    },
  },
  {
    title: 'Assigned User',
    key: 'assignee_user_email',
    width: 220,
    sortOrder: sortOrderFor('assignee_user_email'),
    sorter: true,
    render(row): VNode {
      const isEditing = editingCell.value?.uid === row.uid && editingCell.value.field === 'user';

      if (isEditing) {
        return h(GaiaSelect, {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
          value: row.assignee_user_email || UNASSIGNED_VALUE,
          options: userOptions.value,
          size: 'small',
          placeholder: 'Unassigned',
          style: { minWidth: '200px' },
          disabled: !canEdit.value,
          onUpdateValue: (value: string) => {
            void handleAssignedUserChange(row.uid, value === UNASSIGNED_VALUE ? null : value);
            editingCell.value = null;
          },
          onBlur: () => {
            editingCell.value = null;
          },
          onClick: (event: Event) => {
            event.stopPropagation();
          },
        });
      }

      if (!row.assignee_user_email) {
        return h(
          GaiaText,
          {
            size: 'meta',
            tone: 'muted',
            style: { cursor: canEdit.value ? 'pointer' : 'default' },
            onClick: (event: Event) => {
              startEdit(event, row.uid, 'user');
            },
          },
          () => 'Unassigned'
        );
      }

      const isAgent = row.assignee_user_email === AGENT_EMAIL;
      const user = organizationStore.users.find(u => u.email === row.assignee_user_email);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      const displayName = isAgent ? AGENT_LABEL : user?.full_name || row.assignee_user_email;

      return h(
        'div',
        {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: canEdit.value ? 'pointer' : 'default',
          },
          onClick: (event: Event) => {
            startEdit(event, row.uid, 'user');
          },
        },
        [
          h(GaiaAvatar, { size: 24 }, () => (isAgent ? 'AI' : initialsOf(displayName))),
          h(GaiaText, { size: 'meta', truncate: true }, () => displayName),
        ]
      );
    },
  },
  {
    title: '',
    key: 'actions',
    width: 72,
    render(row): VNode {
      return h(
        GaiaDropdown,
        {
          options: [
            { label: 'Details', key: 'details' },
            { type: 'divider', key: 'delete-divider' },
            { label: 'Delete', key: 'delete', disabled: !canEdit.value, props: gaiaDangerOption },
          ],
          onSelect: (key: string) => {
            if (key === 'details') emit('details', row);
            if (key === 'delete') emit('delete', row);
          },
        },
        () =>
          h(
            GaiaIconButton,
            {
              quaternary: true,
              dimension: 28,
              'aria-label': 'Review actions',
              onClick: (event: Event) => {
                event.stopPropagation();
              },
            },
            () => h(IconMore)
          )
      );
    },
  },
]);

defineExpose({ refresh: fetchReviews, remove: deleteReview });

onMounted(() => void fetchReviews());
</script>

<template>
  <gaia-table
    :columns="columns"
    :data="reviews"
    :loading="loading"
    remote
    :pagination="paginationConfig"
    :row-props="
      (row: ReviewInDb) => ({
        style: 'cursor: pointer',
        onClick: () => emit('open', row),
      })
    "
    @update:sorter="handleSortChange"
  >
    <template #empty>
      <gaia-empty-state title="No reviews yet">
        Start a review to extract and evaluate the terms of a contract.
      </gaia-empty-state>
    </template>
  </gaia-table>
</template>
